import React, { useState, useMemo, useEffect } from "react"
import "./globals.css"
import Header, { ViewMode } from "./components/header"
import Footer from "./components/footer"
import { useClientConfigQuery } from "./hooks/useClientConfig"
import { GridView } from "./components/grid"
import { ListView } from "./components/list"
import { RoadmapItem } from "./models/RoadmapItem"
import { FilterView } from "./components/filterview"
import { ALL_STATUSES_VALUE, DefaultFilterState } from "./components/filterview/FilterView"
import { AsyncLoadedArray } from "./lib/asyncLoaded"
import { loadArrayToState } from "./utils/loadArrayToState"
import { useAuth } from "./lib/stsAuthProvider"
import { addFollow, addLike, getRoadmapData, removeFollow, removeLike } from "./lib/JiraGatewayService"

const ITEMS_PER_PAGE_GRID = 12;
const ITEMS_PER_PAGE_LIST = 10;

function App() {
  console.log("App render")
  const clientConfig = useClientConfigQuery()
  const { user, authAttempted } = useAuth()
  const isAuthenticated = authAttempted && (!!user && !user.expired)
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [page, setPage] = useState(1);
  const [roadmapData, setRoadmapData] = useState(() => new AsyncLoadedArray<RoadmapItem>());
  const [filterState, setFilterState] = useState(DefaultFilterState)
  const [selectedJiraId, setSelectedJiraId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('details');
  });
  const viewingPastReleases = filterState.status === "Released"

  const itemsPerPage = viewMode === 'grid' ? ITEMS_PER_PAGE_GRID : ITEMS_PER_PAGE_LIST;

  const filteredData = useMemo(() => {
    return roadmapData.dataOrEmpty().filter((item) => {
      const categoryMatch = filterState.categories.length === 0
        || filterState.categories.includes(item.category)
      const productMatch = filterState.products.length === 0
        || filterState.products.includes(item.product)
      const statusMatch = filterState.status === ALL_STATUSES_VALUE
        || filterState.status === item.statusOnUI

      const searchString = filterState.searchString.toLowerCase()
      const titleMatch = searchString.length === 0
        || item.title.toLowerCase().includes(searchString)
      const descriptionMatch = searchString.length === 0
        || item.description.toLowerCase().includes(searchString)

      return productMatch && categoryMatch && statusMatch
        && (titleMatch || descriptionMatch)
    })
  }, [roadmapData, filterState])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page, itemsPerPage]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLike = async (jiraId: string, likeIntent: boolean) => {
    const roadmapItem = roadmapData.dataOrEmpty().find(x => x.jiraId === jiraId)
    if (!user || user.expired || !roadmapItem) {
      return
    }

    let newLikeStatus: boolean | undefined
    let newLikeCount: number
    if (likeIntent) {
      if (await addLike(jiraId)) {
        newLikeStatus = true
        newLikeCount = roadmapItem.likeCount + 1
      }
    }
    else {
      if (await removeLike(jiraId)) {
        newLikeStatus = false
        newLikeCount = roadmapItem.likeCount - 1
      }
    }
    if (newLikeStatus === undefined) {
      alert("Following/unfollowing failed. Try again?")
      return
    }

    setRoadmapData(prevData => prevData.withSucceeded(
      prevData.dataOrEmpty().map(item =>
        item.jiraId === jiraId
          ? {
            ...item,
            isLiked: newLikeStatus,
            likeCount: newLikeCount,
          }
          : item
      )
    ))
  };

  const handleFollow = async (jiraId: string, followIntent: boolean) => {
    if (!user || user.expired) {
      return
    }

    let newFollowStatus: boolean | undefined
    if (followIntent) {
      if (await addFollow(jiraId)) {
        newFollowStatus = true
      }
    }
    else {
      if (await removeFollow(jiraId)) {
        newFollowStatus = false
      }
    }
    if (newFollowStatus === undefined) {
      alert("Following/unfollowing failed. Try again?")
      return
    }

    setRoadmapData(prevData => prevData.withSucceeded(
      prevData.dataOrEmpty().map(item =>
        item.jiraId === jiraId
          ? { ...item, isFollowed: newFollowStatus }
          : item
      )
    ))
  };

  const setViewPastReleases = () => {
    setFilterState({ ...filterState, status: "Released" })
  }

  const setViewRoadmap = () => {
    setFilterState(DefaultFilterState)
  }

  const handleFlyoutClose = () => {
    setSelectedJiraId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('details');
    window.history.replaceState({}, '', url.toString());
  };

  useEffect(() => {
    if (selectedJiraId && roadmapData.status === 'succeeded') {
      const data = roadmapData.dataOrEmpty();
      const itemIndex = data.findIndex(item => item.jiraId === selectedJiraId);
      if (itemIndex !== -1) {
        setPage(Math.floor(itemIndex / ITEMS_PER_PAGE_GRID) + 1);
      }
    }
  }, [selectedJiraId, roadmapData.status]);

  useEffect(() => {
    if (clientConfig.isSuccess) { // api requests need clientConfig
      if (roadmapData.status === 'idle' && authAttempted) {
        loadArrayToState(
          () => getRoadmapData(),
          setRoadmapData)
      }
    }
  }, [roadmapData, clientConfig, authAttempted])

  if (!clientConfig.isSuccess && !clientConfig.isError) {
    return <div>Loading...</div>
  }

  return (
    <div className="app-container">
      <Header
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        viewingPastReleases={viewingPastReleases}
      />
      <main className="main-content">
        <FilterView
          roadmapData={roadmapData.dataOrEmpty()}
          filterState={filterState}
          setFilterState={setFilterState}
        />
        {roadmapData.isPending() || (
          viewMode === 'grid' ? (
            <GridView
              data={paginatedData}
              isAuthenticated={isAuthenticated}
              onLike={handleLike}
              onFollow={handleFollow}
              selectedJiraId={selectedJiraId}
              onFlyoutClose={handleFlyoutClose}
            />
          ) : (
            <ListView
              data={paginatedData}
              isAuthenticated={isAuthenticated}
              onLike={handleLike}
              onFollow={handleFollow}
              selectedJiraId={selectedJiraId}
              onFlyoutClose={handleFlyoutClose}
            />
          )
        )}
      </main>
      <Footer
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        viewingPastReleases={viewingPastReleases}
        setViewPastReleases={setViewPastReleases}
        setViewRoadmap={setViewRoadmap}
      />
    </div>
  )
}

export default App